/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter as Router } from 'react-router-dom';
import { ConfirmLeaveModal, ConfirmLeaveModalProps, getConfirmLeaveModalHost } from 'src/core/components/ConfirmLeaveModal';
import { ChangeSavingStateEvent, ConfirmLeaveEvent, ConfirmStayEvent } from 'src/core/types/ConfirmLeaveEvent';
import { GlobalState } from 'src/core/types/GlobalState';
import { GlobalStateContext } from 'src/core/utils/contexts/GlobalStateContext';
import { LocalizationProvider } from 'src/core/utils/contexts/LocalizationProvider';
import { ResponsiveProvider } from 'src/core/utils/contexts/ResponsiveProvider';
import { getEventBusV2 } from 'src/core/utils/eventBusV2';
import { SavingState } from 'src/customText/appshell/dataTaggingPage/hooks/useSaveChanges';

const confirmLeaveModalHost = getConfirmLeaveModalHost();

/**
 * A function to use to confirm navigation.
 * @param message Serialized props (ConfirmLeaveModalProps) for the ConfirmLeaveModal.
 * @param callback Callback for confirming the leave.
 */
const getUserConfirmation = (message: string, callback: (ok: boolean) => void) => {
    const modalProps = JSON.parse(message) as ConfirmLeaveModalProps;
    const eventBusV2 = getEventBusV2();
    let savingState: SavingState = 'saved';
    const root = createRoot(confirmLeaveModalHost);
    const onConfirm = (confirm: boolean) => {
        if (confirm) {
            eventBusV2.emit<ConfirmLeaveEvent>({ kind: 'confirmLeave', id: modalProps.id });
        } else {
            eventBusV2.emit<ConfirmStayEvent>({ kind: 'confirmStay', id: modalProps.id });
        }

        /**
         * Based on bug #27642
         * The user confirmation modal behavior is altered to navigate anyways and fire confirm
         * leave event on user confirmation.
         */

        if (!confirm || !modalProps.dependsOnSavingState) {
            root.unmount();
            callback(modalProps.leaveAnyway ? true : confirm);

            return;
        }

        eventBusV2.on<ChangeSavingStateEvent>({
            kind: 'changeSavingState',
            callback: params => {
                if (params.savingState === 'saved') {
                    root.unmount();
                    if (savingState === 'inProgress') {
                        callback(modalProps.leaveAnyway ? true : confirm);
                    }
                }

                savingState = params.savingState;
            }
        });
    };

    const onDismiss = () => {
        root.unmount();
        callback(false);
    };

    root.render(<ConfirmLeaveModal {...modalProps} onConfirm={onConfirm} onDismiss={onDismiss} />);
};

/**
 * A wrapper around all the providers used by LUIS components.
 */
export const LuisProvider = observer((props: React.PropsWithChildren<GlobalState>) => {
    const { children, dispatcher, rootStore, uiStore } = props;
    const globalProviderValues = React.useMemo(
        () => ({
            dispatcher,
            rootStore,
            uiStore
        }),
        [dispatcher, rootStore, uiStore]
    );

    return (
        <GlobalStateContext.Provider value={globalProviderValues}>
            <LocalizationProvider locale={uiStore.locale.code}>
                <ResponsiveProvider>
                    <Router getUserConfirmation={getUserConfirmation}>{children}</Router>
                </ResponsiveProvider>
            </LocalizationProvider>
        </GlobalStateContext.Provider>
    );
});
